/**
 * lib/utils/domain-vocabulary.js — Domain Vocabulary Service
 *
 * Fetches ACN-specific terms from Supabase `domain_vocabulary` table.
 * Used by:
 *   - STT (Whisper prompt hint for better transcription)
 *   - Voice classifier (entity recognition context)
 *   - Post-transcription correction (misheard → correct mapping)
 *
 * The vocabulary grows over time — new terms can be added via the app,
 * voice pipeline feedback, or manual entry in Supabase.
 */

import { isUnsafeCorrectionSource } from './transcriptStopwords.js';
import { logEvent } from '../obs/log.mjs';

let _cache = null;
let _cacheTime = 0;
const CACHE_TTL = 5 * 60 * 1000; // 5 minutes

/** Categories worth biasing Whisper towards — proper nouns and jargon it mishears. */
const WHISPER_PROMPT_CATEGORIES = ['product', 'material', 'boat_part', 'marine_term', 'person', 'supplier'];

/**
 * The OpenAI Whisper `prompt` field accepts ~224 tokens and, when that is
 * exceeded, silently keeps only the TAIL. Rows arrive ordered by usage_count
 * DESC, so an UNCAPPED join threw away the MOST-used terms — exactly backwards.
 * Measured 2026-07-26: 119 terms -> 1,204 chars -> ~301 tokens, and stt.js then
 * APPENDS the CRM vocab (auto-crm-vocab.js, MAX_TERMS=50) on top of that.
 *
 * We budget here and truncate deliberately, highest-usage first, leaving
 * headroom for that CRM layer rather than letting the API cut blind.
 */
export const WHISPER_PROMPT_TOKEN_BUDGET = 140;

/**
 * Deliberately conservative: marine/Spanish jargon ("guardamancebos", "inox")
 * tokenizes worse than average English, so assume 3 chars/token rather than the
 * usual 4. Over-estimating tokens costs a few terms; under-estimating silently
 * loses the head of the list, which is the failure this guard exists to prevent.
 */
export const WHISPER_PROMPT_CHARS_PER_TOKEN = 3;

/**
 * Ceiling for the FULL prompt after every layer has been concatenated
 * (domain vocabulary + auto-CRM proper nouns). Whisper's hard limit is ~224;
 * we sit under it so an estimation error costs nothing.
 */
export const WHISPER_PROMPT_TOTAL_TOKEN_BUDGET = 200;

/**
 * Clamp the assembled multi-layer prompt. Layers are newline-separated and each
 * is a ", "-joined term list.
 *
 * Splitting the budget evenly and letting under-spending layers donate their
 * slack keeps BOTH layers alive. Trimming from the end instead would delete one
 * layer wholesale — which is the bug being fixed, just with a different victim:
 * today the CRM layer (appended last, so in the tail Whisper keeps) silently
 * consumes the entire domain vocabulary.
 */
export function clampWhisperPrompt(prompt, budgetTokens = WHISPER_PROMPT_TOTAL_TOKEN_BUDGET) {
  if (!prompt) return prompt;
  const maxChars = budgetTokens * WHISPER_PROMPT_CHARS_PER_TOKEN;
  if (prompt.length <= maxChars) return prompt;

  const layers = prompt.split('\n').filter(Boolean);
  if (layers.length === 0) return '';

  const share = Math.floor(maxChars / layers.length);
  const over = layers.filter(l => l.length > share);
  const donated = layers
    .filter(l => l.length <= share)
    .reduce((acc, l) => acc + (share - l.length), 0);
  const perOverChars = over.length ? share + Math.floor(donated / over.length) : share;

  return layers
    .map(l => (l.length <= share
      ? l
      : buildBudgetedWhisperPrompt(l.split(', '), Math.floor(perOverChars / WHISPER_PROMPT_CHARS_PER_TOKEN))))
    .join('\n');
}

/**
 * Rank vocabulary rows for the Whisper prompt, best first.
 *
 * 1. Terms with recorded `misheard_as` variants — empirical proof Whisper gets
 *    them wrong, which is the entire point of biasing the prompt.
 * 2. Then usage_count, fed by real corrections via recordTermUsage().
 * 3. Then alphabetical, purely so the order is stable and testable.
 *
 * Exported for tests: this ordering decides what survives the token budget.
 */
export function rankForWhisperPrompt(a, b) {
  const misheard = t => (Array.isArray(t.misheard_as) && t.misheard_as.length > 0 ? 1 : 0);
  const byMisheard = misheard(b) - misheard(a);
  if (byMisheard !== 0) return byMisheard;
  const byUsage = (b.usage_count || 0) - (a.usage_count || 0);
  if (byUsage !== 0) return byUsage;
  return String(a.term || '').localeCompare(String(b.term || ''));
}

export function buildBudgetedWhisperPrompt(termList, budgetTokens = WHISPER_PROMPT_TOKEN_BUDGET) {
  const maxChars = budgetTokens * WHISPER_PROMPT_CHARS_PER_TOKEN;
  const kept = [];
  let used = 0;
  for (const term of termList) {
    if (!term) continue;
    const cost = kept.length === 0 ? term.length : term.length + 2; // ", "
    if (used + cost > maxChars) break;
    kept.push(term);
    used += cost;
  }
  return kept.join(', ');
}

/** Test-only: clear the in-process cache so tests don't bleed across runs. */
export function _resetVocabCache() {
  _cache = null;
  _cacheTime = 0;
}

/**
 * Fetch all vocabulary terms from Supabase.
 * Caches for 5 minutes to avoid repeated queries.
 */
export async function fetchVocabulary(supabase) {
  const now = Date.now();
  if (_cache && (now - _cacheTime) < CACHE_TTL) return _cache;

  // Ordering drives which terms survive the Whisper prompt budget, so it has to
  // encode value. usage_count ALONE was useless: nothing incremented it (see
  // recordTermUsage), every row sat at 0, and the sort collapsed to alphabetical
  // — keeping "and", "Agree", "Buy" while dropping "teak" and "Sunbrella".
  //
  // misheard_as is the empirical signal available today: a term with recorded
  // mishearings is one Whisper demonstrably gets wrong, which is precisely what
  // the prompt exists to bias against. usage_count now grows from real
  // corrections and refines the order within that group.
  // Only order by usage_count here -- the Whisper-prompt-specific ranking
  // (misheard_as > usage_count > alphabetical) is applied client-side below via
  // rankForWhisperPrompt, so a second DB-level tie-break clause is redundant and
  // several existing test doubles model .order() as a single non-chainable call.
  const { data, error } = await supabase
    .from('domain_vocabulary')
    .select('term, category, misheard_as, definition, spanish, translations, usage_count')
    .order('usage_count', { ascending: false });

  if (error) {
    console.warn('[domain-vocabulary] fetch error:', error.message);
    return _cache || { terms: [], correctionMap: {}, whisperPrompt: '', categories: {} };
  }

  const terms = data || [];
  const correctionMap = {};
  const categories = {};

  // TICKET-480 — a correction whose LEFT side is a real word is data corruption, not a
  // correction. applyCorrections() replaces every \bword\b in the transcript
  // unconditionally, so the live row {term:'knot', misheard_as:['not']} rewrote EVERY
  // English "not" as "knot" (CEO: "the transcript confused no with knot"), and
  // {term:'Source', misheard_as:['por']} did the same to one of the commonest words in
  // Spanish. Rejected entries are NOT silently dropped — they are named in one spine
  // warning per fetch, because the reason this survived is that nothing ever said which
  // corrections were being applied.
  const rejected = [];
  for (const t of terms) {
    // Build correction map
    for (const mis of (t.misheard_as || [])) {
      if (mis.toLowerCase() === t.term.toLowerCase()) continue;
      if (isUnsafeCorrectionSource(mis)) {
        rejected.push({ misheard_as: mis, term: t.term });
        continue;
      }
      correctionMap[mis.toLowerCase()] = t.term;
    }
    // Group by category
    if (!categories[t.category]) categories[t.category] = [];
    categories[t.category].push(t.term);
  }

  if (rejected.length > 0) {
    // spine-emit-ok: telemetry only — a rejected correction must never fail the fetch.
    logEvent({
      source: 'server',
      module: 'voice',
      step: 'vocabulary.correction_rejected',
      status: 'warn',
      error_message: `${rejected.length} unsafe correction(s) rejected from domain_vocabulary`,
      payload: {
        count: rejected.length,
        rejected: rejected.slice(0, 25),
        why: 'misheard_as is an ordinary EN/ES word; replacing it everywhere would corrupt unrelated speech',
      },
    }, { mode: 'lenient' }).catch(() => {});
    console.warn('[domain-vocabulary] rejected unsafe corrections:',
      rejected.map((r) => `"${r.misheard_as}"->"${r.term}"`).join(', '));
  }

  // Whisper prompt: the budget only keeps the head of this list, so the ordering
  // IS the quality decision. PostgREST cannot ORDER BY an expression, so the
  // misheard_as priority is applied here rather than in the query.
  const whisperPrompt = buildBudgetedWhisperPrompt(
    terms
      .filter(t => WHISPER_PROMPT_CATEGORIES.includes(t.category))
      .sort(rankForWhisperPrompt)
      .map(t => t.term)
  );

  _cache = { terms, correctionMap, whisperPrompt, categories };
  _cacheTime = now;
  return _cache;
}

/**
 * Apply STT corrections to transcribed text.
 * Replaces misheard terms with correct ones using word boundary matching.
 */
export function applyCorrections(text, correctionMap) {
  if (!text || !correctionMap) return text;

  let corrected = text;
  for (const [wrong, right] of Object.entries(correctionMap)) {
    // Case-insensitive word boundary replacement
    const regex = new RegExp(`\\b${escapeRegex(wrong)}\\b`, 'gi');
    corrected = corrected.replace(regex, right);
  }
  return corrected;
}

/**
 * Get the Whisper prompt hint string.
 * Pass this as the `prompt` parameter to Whisper API for better domain recognition.
 */
export async function getWhisperPrompt(supabase) {
  const vocab = await fetchVocabulary(supabase);
  return vocab.whisperPrompt;
}

/**
 * Get vocabulary context for the classifier prompt.
 * Returns a formatted string listing known entities by category.
 */
export async function getClassifierContext(supabase) {
  const vocab = await fetchVocabulary(supabase);
  const lines = ['Known ACN domain terms:'];
  for (const [cat, terms] of Object.entries(vocab.categories)) {
    lines.push(`  ${cat}: ${terms.join(', ')}`);
  }
  return lines.join('\n');
}

/**
 * Record that a term was seen in a transcription.
 * Increments usage_count and updates last_seen_at.
 */
export async function recordTermUsage(supabase, term) {
  // Was: .update({ usage_count: supabase.rpc('increment_usage', ...) }) — broken
  // three ways. supabase.rpc() returns a query builder, not a number, so this
  // assigned an object to an integer column; the RPC 'increment_usage' never
  // existed; and nothing in the repo ever called this function. Net effect: all
  // 119 prompt-eligible terms stayed at usage_count = 0, ORDER BY usage_count
  // became a no-op, and vocabulary ranking silently degraded to alphabetical.
  //
  // supabase-js cannot express `usage_count = usage_count + 1`, so the atomic
  // increment lives in the increment_vocabulary_usage() RPC.
  if (!supabase || !term) return null;
  const { data, error } = await supabase.rpc('increment_vocabulary_usage', { p_term: term });
  if (error) {
    // Never block STT on a counter. Surface it rather than swallowing — this
    // file sits outside the spine-emit sentinel glob, which is exactly how the
    // original breakage stayed invisible.
    console.warn('[domain-vocabulary] recordTermUsage failed:', error.message);
    return null;
  }
  return data; // new count, or null when the term is not in the vocabulary
}

/**
 * Add a new term to the vocabulary (e.g., from voice pipeline feedback).
 */
export async function addTerm(supabase, { term, category, misheard_as = [], definition = '', spanish = '', source = 'voice-pipeline' }) {
  const { error } = await supabase
    .from('domain_vocabulary')
    .upsert({
      term,
      category: category || 'custom',
      misheard_as,
      definition,
      spanish,
      source,
      confidence: 0.8,
      last_seen_at: new Date().toISOString(),
    }, { onConflict: 'term,category' });

  if (error) console.warn('[domain-vocabulary] addTerm error:', error.message);
  else _cache = null; // invalidate cache
}

/**
 * Get translation glossary for interpreter.
 * Returns term→translation pairs for a target language from the translations JSONB column.
 * @param {object} supabase
 * @param {string} targetLang - ISO 639-1 code (e.g., 'hi', 'es', 'en')
 * @returns {Promise<Array<{term: string, translation: string}>>}
 */
export async function getTranslationGlossary(supabase, targetLang) {
  const vocab = await fetchVocabulary(supabase);
  const glossary = [];

  for (const t of vocab.terms) {
    const translation = t.translations?.[targetLang];
    if (translation) {
      glossary.push({ term: t.term, translation });
    } else if (targetLang === 'es' && t.spanish) {
      // Fallback: use legacy spanish column if translations JSONB doesn't have it
      glossary.push({ term: t.term, translation: t.spanish });
    }
  }

  return glossary;
}

function escapeRegex(str) {
  return str.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
}
